import { memo, useCallback } from "react";
import type { frameWidget } from "@/elements/frame";
import { AddSlideButton } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/SlideItem/AddSlideButton";
import { SlideContent } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/SlideItem/SlideContent";
import { SlideIndex } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/SlideItem/SlideIndex";
import type { DropPosition } from "../../types";
import styles from "../PresentationView.module.scss";

type SlideItemProps = {
    imgSrc?: string;
    frame: frameWidget;
    uid: string;
    index: number;
    isSelected: boolean;
    isDragOver: boolean;
    dropPosition?: DropPosition;

    onSelect: (uid: string, e: React.MouseEvent<HTMLDivElement>) => void;
    onDragStart?: (uid: string, e: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (uid: string, e: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave?: () => void;
    onDrop?: (uid: string, e: React.DragEvent<HTMLDivElement>) => void;
    onAddFrame?: (index: number) => void;
    isDraggable: boolean;
};

const SlideItemComponent = ({
    imgSrc,
    uid,
    index,
    isSelected,
    isDragOver,
    dropPosition,
    onSelect,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onAddFrame,
    isDraggable,
}: SlideItemProps) => {
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            onSelect(uid, e);
        },
        [uid, onSelect],
    );

    const handleDragStartLocal = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            if (!onDragStart) return;
            onDragStart(uid, e);
        },
        [uid, onDragStart],
    );

    const handleDragOverLocal = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            if (!onDragOver) return;
            onDragOver(uid, e);
        },
        [uid, onDragOver],
    );

    const handleDropLocal = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            if (!onDrop) return;
            onDrop(uid, e);
        },
        [uid, onDrop],
    );

    const handleAddSlideClick = () => {
        if (!onAddFrame) return;
        onAddFrame(index);
    };

    return (
        <div className={styles.slide}>
            <SlideContent
                imgSrc={imgSrc}
                uid={uid}
                isSelected={isSelected}
                isDragOver={isDragOver}
                dropPosition={dropPosition}
                onClick={handleClick}
                onDragStart={handleDragStartLocal}
                onDragOver={handleDragOverLocal}
                onDragLeave={onDragLeave}
                onDrop={handleDropLocal}
                isDraggable={isDraggable}
            />
            <SlideIndex index={index} />
            {onAddFrame ? (
                <AddSlideButton onAddFrame={handleAddSlideClick} />
            ) : (
                <div style={{ height: "24px" }} />
            )}
        </div>
    );
};

export const SlideItem = memo(
    SlideItemComponent,
    (prev, next) =>
        prev.imgSrc === next.imgSrc &&
        prev.uid === next.uid &&
        prev.isSelected === next.isSelected &&
        prev.isDragOver === next.isDragOver &&
        prev.isDraggable === next.isDraggable &&
        (!next.isDragOver || prev.dropPosition === next.dropPosition),
);
